import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import {
  StringCustomFieldConfig,
  SharedModule,
  FormInputComponent,
  DataService,
  NotificationService,
} from '@vendure/admin-ui/core';
import { EmailSubscriptionData, EmailSubscriptionType, defaultEmailSubscriptionData } from '../ui-types';
import { clampUnitInterval, normalizeShadowTransparency, safeJSONParse } from '../utils';
import { Asset } from '../generated';

@Component({
  template: `
    <vdr-card>
      <!-- Type Field -->
      <vdr-form-field [label]="'Type'" class="mb-2">
        <select [formControl]="typeControl">
          <option value="raw">Default</option>
          <option value="omnisend">Omnisend</option>
        </select>
      </vdr-form-field>

      <!-- Background Config -->
      <vdr-form-field [label]="'Background Color'" class="mb-2">
        <input type="color" [formControl]="backgroundColorControl">
      </vdr-form-field>

      <div *ngIf="typeControl.value === 'omnisend'">
        <vdr-form-field [label]="'Omnisend Form ID'" class="mb-2">
          <input type="text" [formControl]="omnisendFormIdControl">
        </vdr-form-field>
      </div>

      <div *ngIf="typeControl.value === 'raw'">
        <vdr-form-field [label]="'Background Image'" class="mb-2">
          <vdr-dynamic-form-input
            [control]="backgroundImageControl"
            [def]="{
              type: 'relation',
              entity: 'Asset',
              ui: {
                component: 'relation-form-input',
              }
            }"
          >
          </vdr-dynamic-form-input>
        </vdr-form-field>
        <vdr-form-field [label]="'Overlay Opacity (0 to 1)'">
          <input type="number" min="0" max="1" step="0.01" [formControl]="overlayOpacityControl">
        </vdr-form-field>
      </div>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class EmailSubscriptionFormComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit {
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  emailSubscriptionData: EmailSubscriptionData;

  typeControl = new FormControl();
  backgroundColorControl = new FormControl();
  backgroundImageControl = new UntypedFormControl();
  overlayOpacityControl = new FormControl();
  // Omnisend FormControls
  omnisendFormIdControl = new FormControl();

  constructor(
    private dataService: DataService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.emailSubscriptionData = safeJSONParse<EmailSubscriptionData>(
      this.formControl.value,
      this.notificationService,
      defaultEmailSubscriptionData
    )!;
    this.initializeFormControls();

    if (this.emailSubscriptionData?.backgroundImageId) {
      this.dataService.product
        .getAsset(this.emailSubscriptionData?.backgroundImageId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.backgroundImageControl.setValue(asset);
        });
    }
  }

  ngAfterViewInit() {
    this.typeControl.valueChanges.subscribe(this.onTypeChange);
    this.backgroundColorControl.valueChanges.subscribe(this.onBackgroundColorChange);
    this.backgroundImageControl.valueChanges.subscribe(this.onBackgroundImageChange);
    this.overlayOpacityControl.valueChanges.subscribe(this.onOverlayOpacityChange);
    this.omnisendFormIdControl.valueChanges.subscribe(this.onOmnisendFormIdChange);
  }

  private initializeFormControls() {
    this.typeControl.setValue(this.emailSubscriptionData?.type || 'raw');
    this.backgroundColorControl.setValue(this.emailSubscriptionData?.backgroundColor || '#FFFFFF');
    const shadowTransparency = normalizeShadowTransparency(this.emailSubscriptionData?.shadowTransparency);
    if (this.emailSubscriptionData) {
      this.emailSubscriptionData.shadowTransparency = shadowTransparency;
    }
    this.overlayOpacityControl.setValue(1.0 - shadowTransparency);
    this.omnisendFormIdControl.setValue(this.emailSubscriptionData?.omnisendFormId || '');
  }

  private onTypeChange = (value: EmailSubscriptionType) => {
    if (this.emailSubscriptionData?.type !== value) {
      this.emailSubscriptionData!.type = value;
      this.updateJsonString();
    }
  };

  private onBackgroundColorChange = (value: string) => {
    if (this.emailSubscriptionData?.backgroundColor !== value) {
      this.emailSubscriptionData!.backgroundColor = value;
      this.updateJsonString();
    }
  };

  private onBackgroundImageChange = (value: Asset) => {
    const backgroundImageId = value ? value.id : null;
    if (this.emailSubscriptionData?.backgroundImageId !== backgroundImageId) {
      this.emailSubscriptionData!.backgroundImageId = backgroundImageId;
      this.updateJsonString();
    }
  };

  private onOverlayOpacityChange = (value: number) => {
    const shadowTransparency = 1.0 - clampUnitInterval(value);
    if (this.emailSubscriptionData?.shadowTransparency !== shadowTransparency) {
      this.emailSubscriptionData!.shadowTransparency = shadowTransparency;
      this.updateJsonString();
    }
  };

  private onOmnisendFormIdChange = (value: string) => {
    if (this.emailSubscriptionData?.omnisendFormId !== value) {
      this.emailSubscriptionData!.omnisendFormId = value;
      this.updateJsonString();
    }
  };

  private updateJsonString() {
    const jsonString = JSON.stringify(this.emailSubscriptionData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
